import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import CustomerAvatar from './CustomerAvatar';
import PaymentModal from '../WorkOrderProfile/PaymentModal';
import MultiOrderPaymentModal from '../WorkOrderProfile/MultiOrderPaymentModal';
import SaveCardModal from '../WorkOrderProfile/SaveCardModal';
import MessageThreadModal from '../Calendar/AppSendText/MessageThreadModal';
import MultiOrderReceipt from '../MultiOrderReceipt';
import { API_URL } from '../../config/api';

import {
    useOrderBalance,
  useBatchedOrderBalances,
  BatchedOrderBalanceDisplay,
  BatchedTotalBalanceDisplay,
  formatCurrency
} from '../hooks/useOrderBalance';
import { formatDateTimeForUser } from '../../utils/dateUtils'
import { STATUS_OPTIONS } from '../../constants/calendar'
import EditableCell from '../EditableCell';
import CustomerEditModal from './CustomerEditModal';
import { getCurrentUser, getAuthHeaders } from '../../utils/auth';
import { ADMIN_ALLOWED_USERS } from '../../utils/userUtils';
import { autoCompleteOrder } from '../../services/autoCompleteService';
import './styles/CustomerProfile.css';



const StatusDropdown = ({ appointmentId, status, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const badgeRef = useRef(null);
  const menuRef = useRef(null);
  const slug = status?.toLowerCase().replace(/\s+/g, '-') ?? '';

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!badgeRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const DROPDOWN_HEIGHT = 260; // approximate max height of the list

  const handleBadgeClick = () => {
    if (!open && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= DROPDOWN_HEIGHT
        ? rect.bottom + 4
        : rect.top - DROPDOWN_HEIGHT - 4;
      setCoords({ top, left: rect.left });
    }
    setOpen(o => !o);
  };

  return (
    <div ref={badgeRef} style={{ display: 'inline-block' }}>
      <span
        className={`status-badge status-${slug}`}
        style={{ cursor: 'pointer' }}
        onClick={handleBadgeClick}
      >
        {status || '—'}
      </span>
      {open && ReactDOM.createPortal(
        <div ref={menuRef} style={{
          position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999,
          background: 'var(--dark-bg)',
          border: '1px solid var(--border-color, #444)',
          borderRadius: 6, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.4)'
        }}>
          {STATUS_OPTIONS.map(s => (
            <div
              key={s}
              style={{ padding: '7px 14px', cursor: 'pointer', fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => { onUpdate(appointmentId, s); setOpen(false); }}
            >
              {s}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const CustomerProfile = ({ currentUser }) => {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMultiPaymentModal, setShowMultiPaymentModal] = useState(false);
  const [showSaveCardModal, setShowSaveCardModal] = useState(false);
  // Orders that just hit a $0 balance from a payment applied here — drives the
  // "complete this order?" prompt (same behavior as the Work Order Profile).
  const [autoCompleteCandidates, setAutoCompleteCandidates] = useState([]);
  const [isAutoCompleting, setIsAutoCompleting] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);

  const isCloverEnabled =
    process.env.REACT_APP_CLOVER_ENABLED === 'true' &&
    process.env.REACT_APP_ENABLE_CARD_ON_FILE === 'true';

  const fetchSavedCards = async () => {
    if (!customerId || !isCloverEnabled) return;
    setCardsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/clover/customer/${customerId}/cards`);
      if (response.data.success) setSavedCards(response.data.cards || []);
    } catch {
      // silently fail
    } finally {
      setCardsLoading(false);
    }
  };
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showMessageThread, setShowMessageThread] = useState(false);

  // Multi-select for printing receipts
  const [selectedOrdersForPrint, setSelectedOrdersForPrint] = useState(new Set());
  const [showMultiReceipt, setShowMultiReceipt] = useState(false);

  // Text invoice link state
  const [isSendingInvoiceSMS, setIsSendingInvoiceSMS] = useState(false);

  // WhatsApp message generator state
  const [whatsappMessage, setWhatsappMessage] = useState(null);
  const [isGeneratingWhatsapp, setIsGeneratingWhatsapp] = useState(false);
  const [whatsappCopied, setWhatsappCopied] = useState(false);

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [isEditingShipping, setIsEditingShipping] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);
  const [isLoadingShipping, setIsLoadingShipping] = useState(false);
  const [notes, setNotes] = useState('');
  const [crown, setCrown] = useState('');
  const [nape, setNape] = useState('');
  const [earToEar, setEarToEar] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [appointmentOffset, setAppointmentOffset] = useState(0);
  const [appointmentTotal, setAppointmentTotal] = useState(0);
  const [hasMoreAppointments, setHasMoreAppointments] = useState(false);
  const [orderOffset, setOrderOffset] = useState(0);
  const [orderTotal, setOrderTotal] = useState(0);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [isTogglingVIP, setIsTogglingVIP] = useState(false);
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);

  // Skip balance loading for stock wig customer (ID 6252)
  const isStockWigCustomer = parseInt(customerId) === 6252;

  // Use the batched balance hook only if not a stock wig customer
  const batchedBalances = useBatchedOrderBalances(orders, orders.length > 0 && !isStockWigCustomer);

  const handlePaymentModalOpen = (order) => {
    console.log('Opening payment modal for order:', order);
    setSelectedOrder(order);
    setShowPaymentModal(true);
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    setSelectedOrder(null);
  };

  const handlePaymentSuccess = (paymentResult) => {
    console.log('Payment successful:', paymentResult);
    const paidOrderNo = selectedOrder?.orderNo;
    setShowPaymentModal(false);
    setSelectedOrder(null);

    fetchCustomerOrders();

    // If this payment cleared the order's balance, offer to complete it.
    if (paidOrderNo) promptCompleteIfPaid([paidOrderNo]);
  };

  // For each order number, re-check the live balance; any that are now paid in
  // full (and not already completed) get queued into the "complete order?" prompt.
  const promptCompleteIfPaid = async (orderNos) => {
    try {
      const paidInFull = [];
      for (const orderNo of orderNos) {
        const balance = await calculateOrderBalance(orderNo);
        const ord = orders.find(o => String(o.orderno) === String(orderNo));
        const alreadyComplete = ord?.OrderStatus?.toLowerCase() === 'completed';
        if (balance <= 0.01 && !alreadyComplete) paidInFull.push(orderNo);
      }
      if (paidInFull.length > 0) setAutoCompleteCandidates(paidInFull);
    } catch (err) {
      console.error('Balance check after payment failed:', err);
    }
  };

  const handleRunAutoComplete = async () => {
    setIsAutoCompleting(true);
    const user = getCurrentUser();
    const results = await Promise.allSettled(
      autoCompleteCandidates.map(orderNo => autoCompleteOrder(orderNo, user))
    );
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    if (ok) toast.success(`${ok} order${ok > 1 ? 's' : ''} completed`);
    if (failed) {
      const firstErr = results.find(r => r.status === 'rejected')?.reason?.message;
      toast.error(`${failed} order${failed > 1 ? 's' : ''} could not be completed${firstErr ? `: ${firstErr}` : ''}`);
    }
    setIsAutoCompleting(false);
    setAutoCompleteCandidates([]);
    fetchCustomerOrders();
    if (batchedBalances?.refetch) batchedBalances.refetch();
  };

  useEffect(() => {
    
    if (customerId) {
      const loadData = async () => {
        setIsLoading(true);
        setError(null);
        
        // IMPORTANT: Load orders first so we have the customer name
        await fetchCustomerOrders();
        
        // Then fetch customer details using the name from orders
        await fetchCustomerData();
        await fetchShippingInfo();
        await fetchSavedCards();
        await fetchAppointments(0);
        
        setIsLoading(false);
      };
      
      loadData();
    }
  }, [customerId]);

  const fetchCustomerData = async () => {
    try {    
      const url = `${API_URL}/api/customers/${customerId}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('⚠️ Customer not found');
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const customerData = await response.json();
      setCustomer(customerData);
      setNotes(customerData.CustomerNotes || '');
      setCrown(customerData.HairlineToCrown || '');
      setNape(customerData.HairlineToNape || '');
      setEarToEar(customerData.EarToEar || '');
          
    } catch (err) {
      console.error('❌ Error in fetchCustomerData:', err);
    }
  };

const fetchAppointments = async (offset = 0) => {
  if (!customerId) return;
  
  try {
    setAppointmentsLoading(true);
    // Use the history endpoint instead
    const url = `${API_URL}/api/customers/${customerId}/appointments/history?offset=${offset}&limit=5`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch appointments');
    }
    
    const data = await response.json();
    setAppointments(data.appointments);
    setAppointmentTotal(data.total);
    setAppointmentOffset(offset);
    setHasMoreAppointments(data.hasMore);

  } catch (err) {
    console.error('Error fetching appointments:', err);
  } finally {
    setAppointmentsLoading(false);
  }
};


// Handlers for pagination
const handleNextAppointments = () => {
  fetchAppointments(appointmentOffset + 5);
};

const handlePrevAppointments = () => {
  fetchAppointments(Math.max(0, appointmentOffset - 5));
};

const updateAppointmentStatus = async (appointmentId, newStatus) => {
  const response = await fetch(`${API_URL}/api/appointments/${appointmentId}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ status: newStatus }),
  });
  if (!response.ok) throw new Error('Failed to update status');
  setAppointments(prev =>
    prev.map(a => a.ID === appointmentId ? { ...a, AppointmentStatus: newStatus } : a)
  );
};

const updateEventDesc = async (appointmentId, eventDesc) => {
  const response = await fetch(`${API_URL}/api/rachels-room/appointment/${appointmentId}/eventDesc`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eventDesc }),
  });
  if (!response.ok) throw new Error('Failed to update event description');
  setAppointments(prev =>
    prev.map(a => a.ID === appointmentId ? { ...a, EventDesc: eventDesc } : a)
  );
};

const updateAppointmentNotes = async (appointmentId, notes) => {
  const response = await fetch(`${API_URL}/api/rachels-room/appointment/${appointmentId}/notes`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes }),
  });
  if (!response.ok) throw new Error('Failed to update appointment notes');
  setAppointments(prev =>
    prev.map(a => a.ID === appointmentId ? { ...a, AppointmentNotes: notes } : a)
  );
};

// Order pagination handlers
const handleNextOrders = () => {
  fetchCustomerOrders(orderOffset + 100, 100);
};

const handlePrevOrders = () => {
  fetchCustomerOrders(Math.max(0, orderOffset - 100), 100);
};


  const fetchShippingInfo = async () => {
    try {
      setIsLoadingShipping(true);
      const response = await axios.get(`${API_URL}/api/customers/${customerId}/shipping`);
      setShippingInfo(response.data.shippingAddress);
      console.log('🚚 Fetched shipping info:', response.data.shippingAddress);
    } catch (err) {
      console.error('Error fetching shipping info:', err);
      // Don't set this as a critical error since shipping info is supplementary
    } finally {
      setIsLoadingShipping(false);
    }
  };
  const handleSaveShipping = async () => {
    if (!shippingInfo.trim()) {
      alert('Shipping address cannot be empty!');
      return;
    }

    try {
      setIsSavingShipping(true);
      await axios.put(`${API_URL}/api/customers/${customerId}/shipping`, {
        shippingAddress: shippingInfo,
      });
      setIsEditingShipping(false);
    } catch (err) {
      console.error('Error saving shipping info:', err);
      alert('Failed to save shipping address');
    } finally {
      setIsSavingShipping(false);
    }
  };


  const fetchCustomerOrders = async (offset = 0, limit = 100) => {
    try {
      setIsLoading(true);
      console.log(`📦 Fetching orders for customer ${customerId} with offset=${offset}, limit=${limit}`);

      const response = await axios.get(`${API_URL}/api/customers/${customerId}/orders?offset=${offset}&limit=${limit}`);

      console.log('📦 Orders API Response:', {
        hasOrders: !!response.data.orders,
        ordersLength: response.data.orders?.length,
        total: response.data.total,
        offset: response.data.offset,
        limit: response.data.limit,
        hasMore: response.data.hasMore
      });

      // API should return { orders: [], total: X, offset: X, limit: X, hasMore: boolean }
      const ordersData = response.data.orders || [];
      const total = response.data.total || 0;

      console.log(`📦 Setting orders: ${ordersData.length} orders, total: ${total}`);

      setOrders(ordersData);
      setOrderTotal(total);
      setOrderOffset(offset);
      setHasMoreOrders(offset + ordersData.length < total);

      // Extract customer info from first order
      if (ordersData.length > 0) {
        const firstOrder = ordersData[0];
        setCustomer({
          ID: customerId,
          CustomerFullName: firstOrder.OrderCustomerName,
          CustomerFirstName: firstOrder.OrderCustomerName?.split(' ')[0] || '',
          CustomerLastName: firstOrder.OrderCustomerName?.split(' ').slice(1).join(' ') || '',
          CustomerPhone: firstOrder.OrderPhone || '',
          CustomerEmail: firstOrder.OrderEmail || '',
          // We'll need to add address fields to your API response
          CustomerAddress: '',
          CustomerCity: '',
          CustomerState: '',
          CustomerZipCode: '',
          MoldId: firstOrder.MoldId || ''
        });
      }

    } catch (err) {
      console.error('❌ Error fetching orders:', err);
      setError('Failed to load customer orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCustomer = () => {
    if (!customer) return;

    setEditFormData({
      ID: customer.ID,
      CustomerFirstName: customer.CustomerFirstName,
      CustomerLastName: customer.CustomerLastName,
      CustomerPhone: customer.CustomerPhone,
      CustomerEmail: customer.CustomerEmail,
      CustomerAddress: customer.CustomerAddress,
      CustomerCity: customer.CustomerCity,
      CustomerState: customer.CustomerState,
      CustomerZipCode: customer.CustomerZipCode,
      MoldId: customer.MoldId,
      bDiniCare: customer.bDiniCare || 0,
      bVIP: customer.bVIP || 0,
      isWholesaler: customer.isWholesaler || 0,
    });
    setIsEditing(true);
  };

  const handleSaveCustomer = (formData, updatedCustomerData) => {
    setCustomer(prev => ({
      ...prev,
      CustomerFullName: updatedCustomerData.customerName,
      CustomerFirstName: formData.CustomerFirstName,
      CustomerLastName: formData.CustomerLastName,
      CustomerPhone: updatedCustomerData.phone,
      CustomerEmail: updatedCustomerData.email,
      CustomerAddress: updatedCustomerData.address,
      CustomerCity: updatedCustomerData.city,
      CustomerState: updatedCustomerData.state,
      CustomerZipCode: updatedCustomerData.zip,
      MoldId: updatedCustomerData.moldId,
      bDiniCare: formData.bDiniCare || 0,
      bVIP: formData.bVIP || 0,
      isWholesaler: formData.isWholesaler || 0,
    }));

    setIsEditing(false);
    toast.success('Customer information updated successfully!');
  };


const handleSaveMeasurements = async () => {
  try {
    const response = await fetch(`${API_URL}/api/customers/${customer.ID}/notes`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        CustomerNotes: notes,
        HairlineToCrown: crown,
        HairlineToNape: nape,
        EarToEar: earToEar,
      }),
    });

    if (response.ok) {
      toast.success('Customer notes and measurements updated!');
      setIsEditingNotes(false); // ✅ exit edit mode
    } else {
      toast.error('Failed to update customer notes.');
    }
  } catch (err) {
    console.error(err);
    toast.error('An error occurred while saving.');
  }
};

  const handleDeleteCustomer = async () => {
    if (!customer || isDeletingCustomer) return;
    if (orders.length > 0 || appointmentTotal > 0) {
      toast.error('This customer has order or appointment history and cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete ${customer.CustomerFullName || 'this customer'} (ID: ${customer.ID})? This cannot be undone.`)) return;

    setIsDeletingCustomer(true);
    try {
      await axios.delete(`${API_URL}/api/customers/${customer.ID}`);
      toast.success('Customer deleted.');
      navigate('/customers');
    } catch (err) {
      if (err.response?.status === 409) {
        const { orders: o = 0, appointments: a = 0 } = err.response.data || {};
        const parts = [];
        if (o) parts.push(`${o} order${o !== 1 ? 's' : ''}`);
        if (a) parts.push(`${a} appointment${a !== 1 ? 's' : ''}`);
        toast.error(`Can't delete — account isn't empty (${parts.join(', ')}).`);
      } else {
        toast.error(err.response?.data?.error || 'Failed to delete customer.');
      }
    } finally {
      setIsDeletingCustomer(false);
    }
  };

  const handleToggleVIP = async () => {
    if (!customer) return;
    const newVal = customer.bVIP ? 0 : 1;
    setIsTogglingVIP(true);
    try {
      const response = await axios.put(`${API_URL}/api/customers/${customerId}/vip`, { bVIP: newVal });
      if (response.data.success) {
        setCustomer(prev => ({ ...prev, bVIP: newVal }));
        toast.success(newVal ? 'Customer marked as VIP' : 'VIP status removed');
      }
    } catch {
      toast.error('Failed to update VIP status');
    } finally {
      setIsTogglingVIP(false);
    }
  };

  // Loyalty stats — derived entirely from already-loaded orders, zero extra API calls
  const loyaltyStats = useMemo(() => {
    if (!orders || orders.length === 0) return null;
    const sorted = [...orders].sort((a, b) => new Date(a.OrderDate) - new Date(b.OrderDate));
    const memberSince = sorted[0]?.OrderDate;
    const lastOrder = sorted[sorted.length - 1]?.OrderDate;
    const monthsSinceLast = lastOrder
      ? Math.floor((Date.now() - new Date(lastOrder)) / (1000 * 60 * 60 * 24 * 30))
      : null;
    return { totalOrders: orderTotal, memberSince, lastOrder, monthsSinceLast };
  }, [orders, orderTotal]);

  // Total spent — derived from batched balances once loaded, no extra calls
  const totalSpent = useMemo(() => {
    if (!batchedBalances || batchedBalances.loading) return null;
    let sum = 0;
    batchedBalances.balances.forEach(b => { sum += b.totalPaid || 0; });
    return sum;
  }, [batchedBalances]);

  // Auto-VIP threshold: $30k spent
  const isAutoVIP = totalSpent !== null && totalSpent >= 30000;

  const handleOrderClick = (orderNo) => {
    const url = `/work-order-profile/${orderNo}`;
    window.open(window.location.origin + url, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };



  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const getStatusClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'completed':
        return 'status-success';
      case 'pending':
      case 'pending order':
        return 'status-warning';
      case 'in progress':
      case 'in process':
        return 'status-info';
      case 'cancelled':
        return 'status-error';
      default:
        return 'status-default';
    }
  };


  if (isLoading) {
    return (
      <div className="customer-profile-loading">
        <div className="spinner-border"></div>
        <p>Loading customer profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-profile-error">
        <div className="alert alert-danger">
          <h4>Error Loading Customer</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/customers')}>
            Back to Customer Search
          </button>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="customer-profile-error">
        <div className="alert alert-warning">
          <h4>Customer Not Found</h4>
          <p>No customer data available.</p>
          <button className="btn btn-primary" onClick={() => navigate('/customers')}>
            Back to Customer Search
          </button>
        </div>
      </div>
    );
  }
  
const calculateOrderBalance = async (orderNo) => {
  try {
    // Fetch items
    const itemsResponse = await axios.get(`${API_URL}/api/orders/${orderNo}/items`);
    const items = itemsResponse.data.items || [];
    const orderTotal = items.reduce((sum, item) => sum + parseFloat(item.OrderItemExtendedCost || 0), 0);

    // Fetch payments
    const paymentsResponse = await axios.get(`${API_URL}/api/orders/${orderNo}/payments`);
    const payments = paymentsResponse.data.payments || [];
    const totalPaid = payments.reduce((sum, payment) => sum + parseFloat(payment.PaymentAmount || 0), 0);

    return orderTotal - totalPaid;
  } catch (error) {
    console.error('Error calculating balance:', error);
    return 0;
  }
};

// Multi-select handlers for printing receipts
const handleToggleOrderSelection = (orderNo, e) => {
  e.stopPropagation(); // Prevent row click from triggering payment modal
  setSelectedOrdersForPrint(prev => {
    const newSet = new Set(prev);
    if (newSet.has(orderNo)) {
      newSet.delete(orderNo);
    } else {
      newSet.add(orderNo);
    }
    return newSet;
  });
};

const handleSelectAllOrders = (e) => {
  e.stopPropagation();
  if (selectedOrdersForPrint.size === orders.length) {
    // Deselect all
    setSelectedOrdersForPrint(new Set());
  } else {
    // Select all
    setSelectedOrdersForPrint(new Set(orders.map(o => o.orderno)));
  }
};

const handlePaySelectedOrders = () => {
  if (selectedOrdersForPrint.size === 0) {
    toast.warning('Please select at least one order to pay');
    return;
  }
  if (batchedBalances.loading) {
    toast.warning('Order balances are still loading, please wait');
    return;
  }
  const selectedWithBalances = Array.from(selectedOrdersForPrint)
    .map(orderNo => {
      const balanceData = batchedBalances.getOrderBalance(orderNo);
      const balance = balanceData?.balanceDue ?? 0;
      return { orderNo, balance };
    })
    .filter(o => o.balance > 0);

  if (selectedWithBalances.length === 0) {
    toast.info('All selected orders have a zero balance');
    return;
  }
  setShowMultiPaymentModal(true);
};

const handleMultiPaymentSuccess = (result) => {
  setShowMultiPaymentModal(false);
  fetchCustomerOrders();

  // Offer to complete any of the just-paid orders whose balance is now $0.
  const successOrderNos = (result?.results || [])
    .filter(r => r.status === 'success')
    .map(r => r.orderNo);
  if (successOrderNos.length > 0) promptCompleteIfPaid(successOrderNos);
};

const handlePrintSelectedReceipts = () => {
  if (selectedOrdersForPrint.size === 0) {
    toast.warning('Please select at least one order to print');
    return;
  }
  setShowMultiReceipt(true);
};

const handleCloseMultiReceipt = () => {
  setShowMultiReceipt(false);
};

// Handler for texting invoice link to customer
const handleTextInvoiceLink = async () => {
  if (selectedOrdersForPrint.size === 0) {
    toast.warning('Please select at least one order to send');
    return;
  }

  if (!customer?.CustomerPhone) {
    toast.error('Customer phone number is required to send invoice link');
    return;
  }

  setIsSendingInvoiceSMS(true);
  try {
    const response = await axios.post(`${API_URL}/api/invoice-link/send-sms`, {
      customerName: customer.CustomerFullName,
      customerPhone: customer.CustomerPhone,
      customerNo: customer.ID,
      orderNumbers: Array.from(selectedOrdersForPrint),
      expiresInHours: 48,
      sentBy: getCurrentUser()
    });

    if (response.data.success) {
      toast.success(`Invoice link sent to ${customer.CustomerPhone}`);
    } else {
      toast.error(response.data.error || 'Failed to send invoice link');
    }
  } catch (error) {
    console.error('Error sending invoice link:', error);
    toast.error('Failed to send invoice link. Please try again.');
  } finally {
    setIsSendingInvoiceSMS(false);
  }
};

// Generate the invoice link message for WhatsApp — same text as SMS but displayed for copy/paste
const handleGenerateWhatsApp = async () => {
  if (selectedOrdersForPrint.size === 0) {
    toast.warning('Please select at least one order');
    return;
  }

  setIsGeneratingWhatsapp(true);
  setWhatsappMessage(null);
  setWhatsappCopied(false);

  try {
    const response = await axios.post(`${API_URL}/api/invoice-link/generate`, {
      customerName: customer.CustomerFullName,
      customerNo: customer.ID,
      orderNumbers: Array.from(selectedOrdersForPrint),
      expiresInHours: 48,
      sentBy: getCurrentUser()
    });

    if (response.data.success) {
      setWhatsappMessage(response.data.message);
    } else {
      toast.error(response.data.error || 'Failed to generate message');
    }
  } catch (error) {
    console.error('Error generating WhatsApp message:', error);
    toast.error('Failed to generate message. Please try again.');
  } finally {
    setIsGeneratingWhatsapp(false);
  }
};

const handleCopyWhatsApp = async () => {
  if (!whatsappMessage) return;
  try {
    await navigator.clipboard.writeText(whatsappMessage);
    setWhatsappCopied(true);
    setTimeout(() => setWhatsappCopied(false), 2500);
  } catch {
    // Fallback for older browsers
    const el = document.createElement('textarea');
    el.value = whatsappMessage;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setWhatsappCopied(true);
    setTimeout(() => setWhatsappCopied(false), 2500);
  }
};

  return (
    <div className="customer-profile-container">
     

        {/* Main Content Grid */}
        <div className="profile-grid">
            {/* Left Column - Customer Info */}
            <div className="customer-info-section">
                {/* Customer Card */}
                <div className="info-card">
                <CustomerAvatar 
                  customer={customer}
                  size="large"
                  clickable={true}
                  onAvatarUpdate={(updatedCustomer) => {
                    setCustomer(updatedCustomer);
                  }}
                />
                <div className="customer-details">
                    <h1 className="customer-name">
                    {customer.CustomerFullName || 'Unknown Customer'}
                    {(customer.bVIP || isAutoVIP) && (
                      <span className="vip-crown-badge" title="VIP Customer">
                        <i className="fas fa-crown"></i> VIP
                      </span>
                    )}
                    {(customer.isWholesaler === 1 || customer.isWholesaler === true) && (
                      <span className="wholesaler-badge" title="Wholesaler">
                        <i className="fas fa-store"></i> Wholesaler
                      </span>
                    )}
                    </h1>
                    <div className="contact-info">
                      {customer.MoldId && (
                        <div className="contact-item">
                        <i className="fas fa-id-badge"></i>
                        <span>{customer.MoldId}</span>
                        </div>
                    )}
                    <div className="contact-item">
                        <i className="fas fa-phone"></i>
                        <span>{formatPhone(customer.CustomerPhone)}</span>
                    </div>
                    {customer.CustomerEmail && (
                        <div className="contact-item">
                        <i className="fas fa-envelope"></i>
                        <span>{customer.CustomerEmail}</span>
                        </div>
                    )}
                    {customer.CustomerAddress && (
                      <div className='contact-item'>
                        <i className="fas fa-map-marker"></i>
                        <span>{customer.CustomerAddress}, {customer.CustomerCity}, {customer.CustomerState}, {customer.CustomerZipCode}</span>
                      </div>
                    )}
                    
                    {/* Shipping Information Card */}
                      <h3 className="shippingH3"><i className="fas fa-shipping-fast"></i> Shipping Address</h3>

                      {isEditingShipping ? (
                        <>
                          <input
                            type="text"
                            value={shippingInfo}
                            onChange={(e) => setShippingInfo(e.target.value)}
                            placeholder="Enter shipping address"
                          />

                          <button
                            onClick={handleSaveShipping}
                            disabled={isSavingShipping}
                          >
                            {isSavingShipping ? 'Saving...' : 'Save'}
                          </button>
                          <button onClick={() => setIsEditingShipping(false)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <p>{shippingInfo || 'No shipping address on file'}</p>
                          <button className="edit-measurements" onClick={() => setIsEditingShipping(true)}>Edit Shipping Address</button>
                        </>
                      )}

                    </div>
                </div>
                </div>
   {/* Top Navigation */}
        <div className="profile-header">
        <button 
            className="back-btn"
            onClick={() => navigate('/customers')}
        >
            <i className="fas fa-arrow-left"></i>
            Back to Search
        </button>
        <button
            className="back-btn"
            onClick={handleEditCustomer}
        >
            <i className="fas fa-edit"></i>
            Edit Customer
        </button>
        {orders.length === 0 && appointmentTotal === 0 && (
            <button
                className="back-btn"
                onClick={handleDeleteCustomer}
                disabled={isDeletingCustomer}
                style={{ color: '#dc3545' }}
                title="Account has no orders or appointments, so it can be deleted"
            >
                <i className={isDeletingCustomer ? 'fas fa-spinner fa-spin' : 'fas fa-trash-alt'}></i>
                {isDeletingCustomer ? 'Deleting...' : 'Delete Customer'}
            </button>
        )}
        </div>
                  {!isStockWigCustomer && (
                    <div className="balance-card">
                      <BatchedTotalBalanceDisplay batchedBalances={batchedBalances} />
                    </div>
                  )}
                  {isStockWigCustomer && (
                    <div className="balance-card">
                      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                        <p>Balance tracking disabled for stock wig inventory</p>
                      </div>
                    </div>
                  )}

              {/* Loyalty block — admin only */}
              {loyaltyStats && ADMIN_ALLOWED_USERS.includes(currentUser?.UserName?.toLowerCase()) && (
                <div className="loyalty-card">
                  <div className="loyalty-header">
                    <span className="loyalty-title"><i className="fas fa-star"></i> Customer Loyalty</span>
                    <button
                      className={`vip-toggle-btn ${customer.bVIP || isAutoVIP ? 'vip-active' : ''}`}
                      onClick={handleToggleVIP}
                      disabled={isTogglingVIP}
                      title={isAutoVIP && !customer.bVIP ? 'Auto-VIP from $25k+ spend — click to manually set/remove' : (customer.bVIP ? 'Remove VIP status' : 'Mark as VIP')}
                    >
                      <i className="fas fa-crown"></i>
                      {customer.bVIP ? 'VIP' : isAutoVIP ? 'Auto-VIP' : 'Set VIP'}
                    </button>
                  </div>
                  <div className="loyalty-stats">
                    <div className="loyalty-stat">
                      <span className="loyalty-stat-num">{loyaltyStats.totalOrders}</span>
                      <span className="loyalty-stat-label">Total Orders</span>
                    </div>
                    {totalSpent !== null && (
                      <div className="loyalty-stat">
                        <span className="loyalty-stat-num">${totalSpent.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                        <span className="loyalty-stat-label">Total Spent</span>
                      </div>
                    )}
                    <div className="loyalty-stat">
                      <span className="loyalty-stat-num">
                        {loyaltyStats.memberSince ? new Date(loyaltyStats.memberSince).getFullYear() : '—'}
                      </span>
                      <span className="loyalty-stat-label">Member Since</span>
                    </div>
                  </div>
                  {loyaltyStats.monthsSinceLast !== null && loyaltyStats.monthsSinceLast >= 12 && (
                    <div className="loyalty-lapsed">
                      <i className="fas fa-clock"></i> Last order {loyaltyStats.monthsSinceLast} months ago
                    </div>
                  )}
                  {loyaltyStats.lastOrder && loyaltyStats.monthsSinceLast < 12 && (
                    <div className="loyalty-recent">
                      <i className="fas fa-check-circle"></i> Last order {new Date(loyaltyStats.lastOrder).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Methods / Saved Cards */}
              {isCloverEnabled && (
                <div className="details-card">
                  <h3><i className="fas fa-credit-card"></i> Payment Methods</h3>

                  {cardsLoading ? (
                    <p style={{ fontSize: '14px', color: '#888' }}>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>
                      Loading cards...
                    </p>
                  ) : savedCards.length === 0 ? (
                    <p style={{ fontSize: '14px', color: '#888', marginBottom: '12px' }}>
                      No cards on file
                    </p>
                  ) : (
                    <div style={{ marginBottom: '12px' }}>
                      {savedCards.map(card => (
                        <div
                          key={card.PaymentMethodId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 10px',
                            marginBottom: '6px',
                            backgroundColor: 'var(--card-bg, var(--dark-bg))',
                            // border: '1px solid var(--border-color, #dee2e6)',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '500' }}>
                              <i className="fas fa-credit-card" style={{ marginRight: '6px', color: '#007bff' }}></i>
                              {card.CardBrand} •••• {card.Last4}
                              <span style={{
                                marginLeft: '6px',
                                backgroundColor: card.IsDebit ? '#6f42c1' : '#007bff',
                                color: 'white',
                                padding: '1px 5px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}>{card.IsDebit ? 'DEBIT' : 'CREDIT'}</span>
                              {!!card.IsExpired && (
                                <span style={{
                                  marginLeft: '4px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  padding: '1px 5px',
                                  borderRadius: '3px',
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}>EXPIRED</span>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                              Expires {card.ExpMonth}/{card.ExpYear}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                            {/* <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#888', cursor: 'pointer', margin: 0 }}>
                              <input
                                type="checkbox"
                                checked={card.IsDefault === 1}
                                onChange={async () => {
                                  if (card.IsDefault === 1) return;
                                  try {
                                    await axios.put(`${API_URL}/api/clover/card/${card.PaymentMethodId}/set-default`, { username: currentUser });
                                    toast.success('Default card updated');
                                    fetchSavedCards();
                                  } catch {
                                    toast.error('Failed to set default');
                                  }
                                }}
                                style={{ accentColor: '#28a745', cursor: card.IsDefault === 1 ? 'default' : 'pointer' }}
                              />
                              Default
                            </label> */}
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Remove ${card.CardBrand} \u2022\u2022\u2022\u2022 ${card.Last4}?`)) return;
                                try {
                                  await axios.delete(`${API_URL}/api/clover/card/${card.PaymentMethodId}`, { data: { username: currentUser } });
                                  toast.success('Card removed');
                                  fetchSavedCards();
                                } catch {
                                  toast.error('Failed to remove card');
                                }
                              }}
                              style={{
                                background: 'none',
                                border: '1px solid #dc3545',
                                color: '#dc3545',
                                borderRadius: '4px',
                                padding: '2px 7px',
                                fontSize: '11px',
                                cursor: 'pointer'
                              }}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    className="open-messages-btn"
                    onClick={() => setShowSaveCardModal(true)}
                  >
                    <i className="fas fa-plus"></i>
                    Save Card on File
                  </button>
                </div>
              )}



      

            </div>

            {/* Right Column - Order History */}
        <div className="orders-section">
          
             <div className="orders-card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>
                  <i className="fas fa-receipt"></i>
                  Order History ({orders.length} orders{orderTotal > orders.length ? ` of ${orderTotal}` : ''})
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  {/* Print Selected Button */}
                  {orders.length > 0 && (
                    <button
                      className="btn btn-primary"
                      onClick={handlePrintSelectedReceipts}
                      disabled={selectedOrdersForPrint.size === 0}
                      style={{
                        opacity: selectedOrdersForPrint.size === 0 ? 0.5 : 1,
                        cursor: selectedOrdersForPrint.size === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <i className="fas fa-print" style={{ marginRight: '8px' }}></i>
                      Print Selected ({selectedOrdersForPrint.size})
                    </button>
                  )}
                  {/* Text Invoice Button */}
                  {orders.length > 0 && (
                    <button
                      className="btn btn-success"
                      onClick={handleTextInvoiceLink}
                      disabled={selectedOrdersForPrint.size === 0 || isSendingInvoiceSMS || !customer?.CustomerPhone}
                      style={{
                        opacity: (selectedOrdersForPrint.size === 0 || !customer?.CustomerPhone) ? 0.5 : 1,
                        cursor: (selectedOrdersForPrint.size === 0 || !customer?.CustomerPhone) ? 'not-allowed' : 'pointer'
                      }}
                      title={!customer?.CustomerPhone ? 'Customer has no phone number' : ''}
                    >
                      <i className="fas fa-sms" style={{ marginRight: '8px' }}></i>
                      {isSendingInvoiceSMS ? 'Sending...' : `Text Invoice (${selectedOrdersForPrint.size})`}
                    </button>
                  )}
                  {/* WhatsApp Message Generator Button */}
                  {orders.length > 0 && (
                    <button
                      className="btn"
                      onClick={handleGenerateWhatsApp}
                      disabled={selectedOrdersForPrint.size === 0 || isGeneratingWhatsapp}
                      style={{
                        opacity: selectedOrdersForPrint.size === 0 ? 0.5 : 1,
                        cursor: selectedOrdersForPrint.size === 0 ? 'not-allowed' : 'pointer',
                        backgroundColor: '#25D366',
                        color: '#fff',
                        border: 'none'
                      }}
                    >
                      <i className="fab fa-whatsapp" style={{ marginRight: '8px' }}></i>
                      {isGeneratingWhatsapp ? 'Generating...' : `Generate WhatsApp (${selectedOrdersForPrint.size})`}
                    </button>
                  )}
                  {/* Pay Selected Button */}
                  {orders.length > 0 && (
                    <button
                      className="btn btn-warning"
                      onClick={handlePaySelectedOrders}
                      disabled={selectedOrdersForPrint.size === 0}
                      style={{
                        opacity: selectedOrdersForPrint.size === 0 ? 0.5 : 1,
                        cursor: selectedOrdersForPrint.size === 0 ? 'not-allowed' : 'pointer',
                        color: '#212529'
                      }}
                    >
                      <i className="fas fa-dollar-sign" style={{ marginRight: '8px' }}></i>
                      Pay Selected ({selectedOrdersForPrint.size})
                    </button>
                  )}
                  {/* DiNi Care Indicator */}
                  {(customer.bDiniCare === 1 || customer.bDiniCare === true) && (
                    <div className="dini-care-badge" style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#ffe0e6', padding: '4px 8px', borderRadius: '4px', marginBottom: '14px' }}>
                      <img
                        src="/images/dini_cares.gif"
                        alt="DiNi Care Customer"
                        style={{
                          width: '80px',
                          height: '50px',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {orders.length === 0 ? (
                <div className="no-orders">
                  <div className="no-orders-icon">
                    <i className="fas fa-inbox"></i>
                  </div>
                  <h4>No Orders Found</h4>
                  <p>This customer has no order history.</p>
                </div>
              ) : (
                <div className="orders-chart">
                  {/* Chart Header Row */}
                  <div className="chart-header">
                    <div className="chart-col select-col-cp" style={{ width: '50px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedOrdersForPrint.size === orders.length && orders.length > 0}
                        onChange={handleSelectAllOrders}
                        title="Select all orders"
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </div>
                    <div className="chart-col order-info-col">Order Info</div>
                    <div className="chart-col type-status-col">WigCode</div>
                    <div className="chart-col type-status-col">Type</div>
                    <div className="chart-col type-status-col">Status</div>
                    <div className="chart-col order-info-col">Date Created</div>
                    {!isStockWigCustomer && (
                      <>
                        <div className="chart-col amount-col">Starting Amount</div>
                        <div className="chart-col payments-col">Total Payments</div>
                        <div className="chart-col balance-col">Balance Due</div>
                        <div className="chart-col last-payment-col">Last Payment</div>
                      </>
                    )}
                  </div>
                  
                  {/* Order Rows */}
                  <div className="chart-body">
                    {orders.map((order, index) => {
                      const balanceData = batchedBalances.getOrderBalance(order.orderno);
                      
                      return (
                        <div
                          key={index}
                          className={`chart-row ${selectedOrdersForPrint.has(order.orderno) ? 'selected-for-print' : ''}`}
                          onClick={async (e) => {
                            if (!e.target.closest('.order-number-link') && !e.target.closest('.order-select-checkbox')) {
                              const actualBalance = await calculateOrderBalance(order.orderno);

                              handlePaymentModalOpen({
                                orderNo: order.orderno,
                                balance: actualBalance,
                                customerName: customer.CustomerFullName,
                              });
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {/* Checkbox Column */}
                          <div className="chart-col select-col-cp order-select-checkbox" style={{ width: '50px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedOrdersForPrint.has(order.orderno)}
                              onChange={(e) => handleToggleOrderSelection(order.orderno, e)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </div>
                          {/* Column 1: Order Info (Order#, Barcode, Date stacked) */}

                          <div className="chart-col order-info-col">
                            <div 
                              className="order-number order-number-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOrderClick(order.orderno);
                              }}
                              style={{ 
                                cursor: 'pointer', 
                                color: '#007bff', 
                                textDecoration: 'underline',
                                fontWeight: 'bold',
                                fontSize: '18px'
                              }}
                            >
                              #{order.orderno}
                            </div>
                          
                          </div>
            {/* Column 2: Barcode) */}
                            <div className="chart-col type-status-col">
                              <div className="order-barcode" >
                                {order.Wig_Bar_Code}
                              </div>                
                          </div>

            {/* Column 3: Type (stacked) */}
                          <div className="chart-col type-status-col">
                            <div className="order-type" style={{ fontSize: '18px', fontWeight: '500' }}>
                              {order.OrderWigType || order.orderwigtype || 'N/A'}
                            </div>

                          </div>
                        
                          {/* Column 4: Type & Status (stacked) */}
                          <div className="chart-col type-status-col">
                            <div 
                              className={`order-status ${getStatusClass(order.OrderStatus)}`}
                              style={{ fontSize: '15px', marginTop: '4px' }}
                            >
                              {order.OrderStatus || 'Unknown'}
                            </div>
                          </div>
                          {/* Column 4: Date Created */}
                          <div className="chart-col order-info-col">
                            <div className="order-date" style={{ fontSize: '15px', color: '#999', marginTop: '2px' }}>
                              {formatDate(order.OrderDate)}
                            </div>
                          </div>

                          {!isStockWigCustomer && (
                            <>
                              {/* Column 3: Starting Amount */}
                              <div className="chart-col amount-col">
                                <div className="starting-amount">
                                  {balanceData && !balanceData.loading ? (
                                    <span className="currency-amount" style={{ fontWeight: '600', fontSize: '20px' }}>
                                      ${balanceData.orderTotal.toFixed(2)}
                                    </span>
                                  ) : (
                                    <span className="loading-text" style={{ color: '#999', fontSize: '20px' }}>
                                      Loading...
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Column 4: Total Payments */}
                              <div className="chart-col payments-col">
                                <div className="total-payments">
                                  {balanceData && !balanceData.loading ? (
                                    <span
                                      className="currency-amount"
                                      style={{
                                        fontWeight: '600',
                                        fontSize: '20px',
                                        color: balanceData.totalPaid > 0 ? '#28a745' : '#6c757d'
                                      }}
                                    >
                                      ${balanceData.totalPaid.toFixed(2)}
                                    </span>
                                  ) : (
                                    <span className="loading-text" style={{ color: '#999', fontSize: '20px' }}>
                                      Loading...
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Column 5: Balance Due */}
                              <div className="chart-col balance-col">
                                <div className="balance-due currency-amount">
                                  <BatchedOrderBalanceDisplay
                                    orderNo={order.orderno}
                                    batchedBalances={batchedBalances}
                                  />
                                </div>
                              </div>

                              {/* Column 6: Last Payment Date */}
                              <div className="chart-col last-payment-col">
                                <div className="last-payment-date" >
                                  {balanceData && balanceData.lastPaymentDate ? (
                                    new Date(balanceData.lastPaymentDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })
                                  ) : (
                                    'No payments'
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Order Pagination Controls */}
              {orderTotal > orders.length && (
                <div className="pagination-controls" style={{ marginTop: '20px', padding: '15px', borderTop: '1px solid #ddd' }}>
                  <button
                    onClick={handlePrevOrders}
                    disabled={orderOffset === 0}
                    className="pagination-btn"
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous
                  </button>

                  <span className="pagination-info">
                    Showing {orderOffset + 1} - {Math.min(orderOffset + orders.length, orderTotal)} of {orderTotal}
                  </span>

                  <button
                    onClick={handleNextOrders}
                    disabled={!hasMoreOrders}
                    className="pagination-btn"
                  >
                    Next
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>

            <div className="profile-columns-row">
              <div className="profile-side-col">
              <div className="details-card notes-section">
                <h3><i className="fas fa-sticky-note"></i>Notes & Measurements</h3>

                {isEditingNotes ? (
                  <>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Enter customer notes..."
                    />
                    <input
                      value={crown}
                      onChange={(e) => setCrown(e.target.value)}
                      placeholder="Hairline to Crown"
                    />
                    <input
                      value={nape}
                      onChange={(e) => setNape(e.target.value)}
                      placeholder="Hairline to Nape"
                    />
                    <input
                      value={earToEar}
                      onChange={(e) => setEarToEar(e.target.value)}
                      placeholder="Ear to Ear"
                    />

                    <button onClick={handleSaveMeasurements}>Save</button>
                    <button onClick={() => setIsEditingNotes(false)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <p>{notes || 'No notes available'}</p>
                    <p className="measurement-label">Hairline to Crown: {crown}</p>
                    <p className="measurement-label">Hairline to Nape: {nape}</p>
                    <p className="measurement-label">Ear to Ear: {earToEar}</p>
                    <button className="edit-measurements" onClick={() => setIsEditingNotes(true)}>Edit</button>
                  </>
                )}
              </div>

              <div className="details-card message-section">
                <h3><i className="fas fa-comments"></i> Messages</h3>
                <button
                  className="open-messages-btn"
                  onClick={() => setShowMessageThread(true)}
                  disabled={!customer?.CustomerPhone}
                >
                  <i className="fas fa-sms"></i>
                  {customer?.CustomerPhone ? 'Open Message Thread' : 'No Phone Number'}
                </button>
              </div>
            </div>
              
            <div className="orders-card profile-main-col">
              
  <div className="card-header">
    <h3>
      <i className="fas fa-calendar-check"></i>
      Appointment History
    </h3>
    {appointmentTotal > 0 && (
      <span className="total-count">{appointmentTotal} total</span>
    )}
  </div>

  <div className="orders-chart">
    {appointmentsLoading ? (
      <div className="loading">Loading appointments...</div>
    ) : appointments.length === 0 ? (
      <p className="no-data">No appointments found</p>
    ) : (
      <>
        <div >
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Title</th>
                <th>Meeting With</th>
                <th>Event Description</th>
                <th>Appointment Notes</th>
                <th>Status</th>
                <th>OrderNo</th>
              </tr>
            </thead>
           <tbody>
  {appointments.map((apt, index) => (
    <tr key={index}>
      <td>{formatDateTimeForUser(apt.EventStart)}</td>
      <td>{apt.title}</td>
      <td>{apt.MeetingWith}</td>
      <td>
        <EditableCell
          value = {apt.EventDesc}
          onSave={(value) => updateEventDesc(apt.ID, value)}
        />
      </td>
      <td>
        <EditableCell
          value = {apt.AppointmentNotes}
          onSave={(value) => updateAppointmentNotes(apt.ID, value)}
          multiline={true}
        />
      </td>
      <td>
        <StatusDropdown
          appointmentId={apt.ID}
          status={apt.AppointmentStatus}
          onUpdate={updateAppointmentStatus}
        />
      </td>
      <td>
        {apt.OrderNo ? (
          <span
          style={{ 
                  cursor: 'pointer', 
                  color: '#007bff', 
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}
          onClick={(e) => {
                e.stopPropagation();
                handleOrderClick(apt.OrderNo);
              }}>{apt.OrderNo}</span>
        ) : (
          <span>-</span>
           
        )}
      </td>

    </tr>
  ))}
</tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {appointmentTotal > 5 && (
          <div className="pagination-controls">
            <button
              onClick={handlePrevAppointments}
              disabled={appointmentOffset === 0}
              className="pagination-btn"
            >
              <i className="fas fa-chevron-left"></i>
              Previous
            </button>

            <span className="pagination-info">
              Showing {appointmentOffset + 1} - {Math.min(appointmentOffset + 5, appointmentTotal)} of {appointmentTotal}
            </span>

            <button
              onClick={handleNextAppointments}
              disabled={!hasMoreAppointments}
              className="pagination-btn"
            >
              Next
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}
      </>
    )}
  </div>
</div>

         

            </div>


     
          </div>
        {/* Customer Edit Modal */}
        <CustomerEditModal
        isEditing={isEditing}
        editFormData={editFormData}
        onClose={() => setIsEditing(false)}
        onSave={handleSaveCustomer}
        />

        {showPaymentModal && selectedOrder && (
          <PaymentModal
            orderNo={selectedOrder.orderNo}
            currentBalance={selectedOrder.balance}
            customerId={parseInt(customerId)} // ✅ Pass customerId for Clover integration
            onClose={handlePaymentModalClose}
            onPaymentSuccess={handlePaymentSuccess}
            // ✅ Remove taxInfo prop - let PaymentModal fetch it
          />
        )}

        {showMultiPaymentModal && (
          <MultiOrderPaymentModal
            orders={Array.from(selectedOrdersForPrint).map(orderNo => {
              const balanceData = batchedBalances.getOrderBalance(orderNo);
              return { orderNo, balance: balanceData?.balanceDue ?? 0 };
            }).filter(o => o.balance > 0)}
            customerId={parseInt(customerId)}
            onClose={() => setShowMultiPaymentModal(false)}
            onPaymentSuccess={handleMultiPaymentSuccess}
          />
        )}

        {autoCompleteCandidates.length > 0 && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10001
          }}>
            <div style={{
              background: '#1e1e2e', border: '1px solid #444', borderRadius: '12px',
              padding: '28px', width: '400px', maxWidth: '90vw',
              display: 'flex', flexDirection: 'column', gap: '16px'
            }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '16px' }}>
                ✅ {autoCompleteCandidates.length > 1 ? 'Orders' : 'Order'} Fully Paid
              </h3>
              <p style={{ margin: 0, color: '#aaa', fontSize: '13px', lineHeight: '1.5' }}>
                {autoCompleteCandidates.length > 1 ? (
                  <>Orders <strong style={{ color: '#fff' }}>#{autoCompleteCandidates.join(', #')}</strong> are
                  now paid in full. Would you like to complete them?</>
                ) : (
                  <>The balance on order <strong style={{ color: '#fff' }}>#{autoCompleteCandidates[0]}</strong> is
                  now paid in full. Would you like to complete this order?</>
                )}
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setAutoCompleteCandidates([])}
                  disabled={isAutoCompleting}
                  style={{
                    padding: '8px 16px', borderRadius: '6px', border: '1px solid #555',
                    background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: '13px'
                  }}
                >
                  Not Yet
                </button>
                <button
                  onClick={handleRunAutoComplete}
                  disabled={isAutoCompleting}
                  style={{
                    padding: '8px 16px', borderRadius: '6px', border: 'none',
                    background: '#10b981', color: '#fff', cursor: 'pointer',
                    fontSize: '13px', fontWeight: '600'
                  }}
                >
                  {isAutoCompleting
                    ? 'Completing…'
                    : (autoCompleteCandidates.length > 1 ? 'Complete Orders' : 'Complete Order')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showSaveCardModal && (
          <SaveCardModal
            customerId={parseInt(customerId)}
            currentUsername={getCurrentUser()}
            onClose={() => setShowSaveCardModal(false)}
            onCardSaved={() => fetchSavedCards()}
          />
        )}

        {/* Message Thread Modal */}
        {showMessageThread && customer && (
          <MessageThreadModal
            phoneNumber={customer.CustomerPhone}
            customerName={customer.CustomerFullName}
            appointmentId={null}
            onClose={() => setShowMessageThread(false)}
          />
        )}

        {/* Multi-Order Receipt Print Modal */}
        {showMultiReceipt && (
          <MultiOrderReceipt
            orderNumbers={Array.from(selectedOrdersForPrint)}
            customerInfo={customer}
            isVisible={showMultiReceipt}
            onClose={handleCloseMultiReceipt}
          />
        )}

        {/* WhatsApp Message Modal */}
        {whatsappMessage && (
          <div
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1050
            }}
            onClick={() => setWhatsappMessage(null)}
          >
            <div
              style={{
                background: 'var(--dark-surface, #1e1e2e)',
                border: '1px solid var(--border-color, #444)',
                borderRadius: '12px',
                padding: '24px',
                width: '480px',
                maxWidth: '95vw',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '16px' }}>
                  <i className="fab fa-whatsapp" style={{ color: '#25D366', fontSize: '20px' }}></i>
                  WhatsApp Message
                </h5>
                <button
                  onClick={() => setWhatsappMessage(null)}
                  style={{ background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>

              <p style={{ fontSize: '13px', color: '#888', marginBottom: '10px' }}>
                Copy this message and paste it into your WhatsApp chat with the customer.
              </p>

              <textarea
                readOnly
                value={whatsappMessage}
                onClick={e => e.target.select()}
                style={{
                  width: '100%',
                  minHeight: '110px',
                  background: 'var(--dark-bg, #12121f)',
                  border: '1px solid var(--border-color, #444)',
                  borderRadius: '8px',
                  color: 'var(--text-primary, #e0e0e0)',
                  padding: '12px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  resize: 'none',
                  boxSizing: 'border-box',
                  cursor: 'text'
                }}
              />

              <div style={{ display: 'flex', gap: '10px', marginTop: '14px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setWhatsappMessage(null)}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border-color, #444)',
                    color: '#888',
                    padding: '8px 18px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Close
                </button>
                <button
                  onClick={handleCopyWhatsApp}
                  style={{
                    backgroundColor: whatsappCopied ? '#28a745' : '#25D366',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 22px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <i className={`fas fa-${whatsappCopied ? 'check' : 'copy'}`}></i>
                  {whatsappCopied ? 'Copied!' : 'Copy Message'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>

   {/* Horizontal Order Timeline */}
        {/* {orders.length > 0 && (
          <OrderTimeline orders={orders} onOrderClick={handleOrderClick} batchedBalances={batchedBalances} />
        )} */}
    </div>
  );
};

const ACTIVE_STATUSES = new Set([
  'in progress', 'in process', 'active', 'pending', 'pending order',
  'in manufacturing', 'in production', 'processing', 'open',
]);

const WIG_TYPE_ABBREV = {
  'new wig': 'New',
  'wig repair': 'Repair',
  'wash & set': 'W&S',
  'accessory': 'Acc.',
  'other': 'Other',
};

function getWigAbbrev(type) {
  if (!type) return '';
  return WIG_TYPE_ABBREV[type.toLowerCase()] ?? type.slice(0, 5);
}

const GAP_THRESHOLD_MS = 8 * 30 * 24 * 60 * 60 * 1000; // 8 months

function getStatusClass(status, balanceDue) {
  if (balanceDue > 0) return 'timeline-dot-balance';
  const s = status?.toLowerCase() ?? '';
  if (s === 'completed') return 'timeline-dot-completed';
  if (s === 'cancelled') return 'timeline-dot-cancelled';
  if (ACTIVE_STATUSES.has(s) || s !== '') return 'timeline-dot-inprogress';
  return 'timeline-dot-default';
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// function OrderTimeline({ orders, onOrderClick, batchedBalances }) {
//   const [hovered, setHovered] = useState(null);
//   const closeTimer = useRef(null);

//   const handleEnter = (order) => {
//     clearTimeout(closeTimer.current);
//     setHovered(order);
//   };

//   const handleLeave = () => {
//     closeTimer.current = setTimeout(() => setHovered(null), 200);
//   };

//   const handlePreviewEnter = () => clearTimeout(closeTimer.current);
//   const handlePreviewLeave = () => {
//     closeTimer.current = setTimeout(() => setHovered(null), 200);
//   };

//   // Most recent 30, oldest→newest
//   const timelineOrders = useMemo(() => {
//     return [...orders]
//       .sort((a, b) => new Date(a.OrderDate) - new Date(b.OrderDate))
//       .slice(-30);
//   }, [orders]);

//   // Proportional positions based on real dates
//   const { positions, gaps, yearMarkers } = useMemo(() => {
//     if (timelineOrders.length === 0) return { positions: [], gaps: [], yearMarkers: [] };

//     const dates = timelineOrders.map(o => o.OrderDate ? new Date(o.OrderDate).getTime() : 0);
//     const minDate = Math.min(...dates);
//     const maxDate = Math.max(...dates);
//     const range = maxDate - minDate || 1;

//     const positions = dates.map(d => ((d - minDate) / range) * 96 + 2); // 2–98% to avoid edge clipping

//     // Gap labels between consecutive orders > 8 months apart
//     const gaps = [];
//     for (let i = 1; i < dates.length; i++) {
//       const diff = dates[i] - dates[i - 1];
//       if (diff > GAP_THRESHOLD_MS) {
//         const months = Math.round(diff / (30 * 24 * 60 * 60 * 1000));
//         gaps.push({ midPct: (positions[i - 1] + positions[i]) / 2, months });
//       }
//     }

//     // Year markers at the proportional position of first order of each year
//     const seen = new Set();
//     const yearMarkers = timelineOrders.reduce((acc, order, idx) => {
//       const yr = order.OrderDate ? new Date(order.OrderDate).getFullYear() : null;
//       if (yr && !seen.has(yr)) { seen.add(yr); acc.push({ year: yr, pct: positions[idx] }); }
//       return acc;
//     }, []);

//     return { positions, gaps, yearMarkers };
//   }, [timelineOrders]);

//   const isActive = (status) => ACTIVE_STATUSES.has(status?.toLowerCase() ?? '');

//   const hoveredBalance = hovered ? batchedBalances?.getOrderBalance(hovered.orderno) : null;
//   const hoveredBalanceDue = hoveredBalance?.balanceDue ?? 0;
//   const hoveredDotClass = hovered ? getStatusClass(hovered.OrderStatus, hoveredBalanceDue) : '';

//   return (
//     <div className="order-timeline-section">
//       <div className="order-timeline-header">
//         <span><i className="fas fa-route"></i> Order Journey</span>
//         <div className="order-timeline-header-right">
//           <span className="order-timeline-stat">{timelineOrders.length} orders shown</span>
//           {batchedBalances && !batchedBalances.loading && (
//             <span className="order-timeline-stat">
//               {timelineOrders.filter(o => { const b = batchedBalances.getOrderBalance(o.orderno); return b && !b.error && b.balanceDue > 0; }).length} with balance
//             </span>
//           )}
//           <span className="order-timeline-stat">
//             {timelineOrders.filter(o => isActive(o.OrderStatus)).length} active
//           </span>
//         </div>
//       </div>

//       <div className="order-timeline-track-wrap">
//         <div className="order-timeline-track">
//           <div className="order-timeline-line" />

//           {/* Year markers */}
//           {yearMarkers.map(({ year, pct }) => (
//             <div key={`yr-${year}`} className="order-timeline-year" style={{ left: `${pct}%` }}>
//               {year}
//             </div>
//           ))}

//           {/* Gap indicators */}
//           {gaps.map(({ midPct, months }, i) => (
//             <div key={`gap-${i}`} className="order-timeline-gap" style={{ left: `${midPct}%` }}>
//               <div className="order-timeline-gap-line" />
//               <span className="order-timeline-gap-label">{months}mo gap</span>
//             </div>
//           ))}

//           {/* Dots */}
//           {timelineOrders.map((order, idx) => {
//             const balance = batchedBalances?.getOrderBalance(order.orderno);
//             const balanceDue = balance?.balanceDue ?? 0;
//             const dotClass = getStatusClass(order.OrderStatus, balanceDue);
//             const active = isActive(order.OrderStatus);
//             const isHov = hovered?.orderno === order.orderno;
//             const above = idx % 2 === 0; // alternate above/below the line
//             const dateStr = order.OrderDate
//               ? new Date(order.OrderDate).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
//               : '';
//             const abbrev = getWigAbbrev(order.OrderWigType);
//             return (
//               <div
//                 key={order.orderno}
//                 className={`order-timeline-stop${isHov ? ' stop-hovered' : ''}${above ? ' stop-above' : ' stop-below'}`}
//                 style={{ left: `${positions[idx]}%` }}
//                 onMouseEnter={() => handleEnter(order)}
//                 onMouseLeave={handleLeave}
//                 onClick={() => onOrderClick(order.orderno)}
//               >
//                 {above && (
//                   <div className="tl-labels tl-labels-above">
//                     {abbrev && <div className="order-timeline-sublabel">{abbrev}</div>}
//                     <div className="order-timeline-label">{dateStr}</div>
//                   </div>
//                 )}
//                 <div className={`order-timeline-dot ${dotClass}${active ? ' dot-pulse' : ''}`} />
//                 {!above && (
//                   <div className="tl-labels tl-labels-below">
//                     {abbrev && <div className="order-timeline-sublabel">{abbrev}</div>}
//                     <div className="order-timeline-label">{dateStr}</div>
//                   </div>
//                 )}
//               </div>
//             );
//           })}
//         </div>
//       </div>

//       {/* Mini preview panel — renders below the track, no clipping */}
//       <div className={`order-timeline-preview${hovered ? ' preview-visible' : ''}`} onMouseEnter={handlePreviewEnter} onMouseLeave={handlePreviewLeave}>
//         {hovered && (
//           <>
//             <div className="preview-header">
//               <div className="preview-order-num">
//                 <i className="fas fa-file-alt"></i> Order #{hovered.orderno}
//                 <span className={`preview-status-pill tip-status-${hoveredDotClass}`}>{hovered.OrderStatus || 'Unknown'}</span>
//               </div>
//               <button className="preview-open-btn" onClick={() => onOrderClick(hovered.orderno)}>
//                 Open Order <i className="fas fa-arrow-right"></i>
//               </button>
//             </div>
//             <div className="preview-body">
//               {hovered.OrderWigType && (
//                 <div className="preview-row">
//                   <span className="preview-label"><i className="fas fa-tag"></i> Type</span>
//                   <span className="preview-val">{hovered.OrderWigType}</span>
//                 </div>
//               )}
//               {hovered.Wig_Bar_Code && (
//                 <div className="preview-row">
//                   <span className="preview-label"><i className="fas fa-barcode"></i> Barcode</span>
//                   <span className="preview-val">{hovered.Wig_Bar_Code}</span>
//                 </div>
//               )}
//               <div className="preview-row">
//                 <span className="preview-label"><i className="fas fa-calendar-plus"></i> Created</span>
//                 <span className="preview-val">{fmtDate(hovered.OrderDate)}</span>
//               </div>
//               {hovered.DueDate && (
//                 <div className="preview-row">
//                   <span className="preview-label"><i className="fas fa-flag-checkered"></i> Due</span>
//                   <span className="preview-val">{fmtDate(hovered.DueDate)}</span>
//                 </div>
//               )}
//               {hovered.Wig_Warranty_Status && (
//                 <div className="preview-row">
//                   <span className="preview-label"><i className="fas fa-shield-alt"></i> Warranty</span>
//                   <span className="preview-val">{hovered.Wig_Warranty_Status}
//                     {hovered.Wig_Warranty_Expires_Date && ` · expires ${fmtDate(hovered.Wig_Warranty_Expires_Date)}`}
//                   </span>
//                 </div>
//               )}
//               {hoveredBalance && !hoveredBalance.error && (
//                 <div className="preview-row">
//                   <span className="preview-label"><i className="fas fa-dollar-sign"></i> Balance</span>
//                   <span className="preview-val" style={{ color: hoveredBalanceDue > 0 ? 'var(--error-color)' : 'var(--green-accent)', fontWeight: 600 }}>
//                     {hoveredBalanceDue > 0 ? `$${hoveredBalanceDue.toFixed(2)} due` : 'Paid in full'}
//                   </span>
//                 </div>
//               )}
//             </div>
//           </>
//         )}
//       </div>

//       <div className="order-timeline-legend">
//         <span className="legend-item"><span className="legend-dot timeline-dot-completed"></span> Completed &amp; Paid</span>
//         <span className="legend-item"><span className="legend-dot timeline-dot-inprogress dot-pulse-static"></span> Active / In Progress</span>
//         <span className="legend-item"><span className="legend-dot timeline-dot-balance"></span> Balance Due</span>
//         <span className="legend-item"><span className="legend-dot timeline-dot-cancelled"></span> Cancelled</span>
//         <span className="legend-item order-timeline-gap-legend"><span className="gap-legend-line"></span> Gap &gt;8 months</span>
//       </div>
//     </div>
//   );
// }

export default CustomerProfile;